import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";

// API's
import { getTechnicianSecondariesPayRates } from "@/PayPlans/api/technicians/queries";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// MUI
import { Button, Skeleton, Stack, Typography } from "@mui/material";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

export default function RetentionPay(props) {
	const {
		sectionId = "retentionPay",
		position = "",
		content = {
			retention: {
				input: "Dollar",
				value: 0,
			},
			isGuaranteedDraw: false,
		},
		disabled = false,
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;

	const {
		data: payRateData = [],
		isLoading: isLoadingPayRate,
		error: errorPayRate,
		refetch,
	} = useQuery({
		queryKey: ["technicianPayRatesSecondaries", locationId, position],
		queryFn: async () => {
			const result = await getTechnicianSecondariesPayRates(locationId, position);
			const secondaries = result?.secondaries ?? [];
			return secondaries.map((item) => ({ value: String(item), label: `$ ${item.toFixed(2)}` })) ?? [];
		},
		staleTime: Infinity,
		enabled: false, // Don't fetch data on component mount
	});

	const [payRateSelected, setPayRateSelected] = useState(null);

	// Used to add errors to the pay plan context
	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrors = errors?.get("retention");
	const errorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onRetentionPayUpdate = (updatedRetentionPay) => {
		isValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(sectionId, null, updatedRetentionPay);
		}
	};

	// States
	const [retentionPay, setRetentionPay] = useCallbackState(
		{
			retention: {
				input: "Dollar",
				value: 0,
			},
			isGuaranteedDraw: false,
		},
		onRetentionPayUpdate
	);

	const update = (updatedRetentionPay) => {
		setRetentionPay(updatedRetentionPay);
		wasUpdated.current = true;
	};

	const handleSelectChange = (param) => {
		handleUpdateInput("retention", "value", param.value);
		setPayRateSelected(param.value);
	};

	const handleButtonClick = () => {
		refetch(); // This will trigger the query
	};

	const handleUpdateInput = (area, field, value) => {
		const updated = { ...retentionPay };
		if (area === "isGuaranteedDraw") {
			updated[area] = value;
		} else if (field === "input") {
			updated[area][field] = value;
			updated[area]["value"] = 0;
		} else {
			updated[area][field] = value ? Number(value) : 0;
		}
		update(updated);
	};

	useMemo(() => {
		if (retentionPay) {
			validate([
				{
					definition: {
						useKey: "retention",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									distinct: 0,
								},
							],
						},
						errorsMap: ["retention"],
						message: "Invalid commission",
						validationType: "SIMPLE",
					},
					section: [retentionPay],
				},
			]);
		}
	}, [retentionPay, validate]);

	useEffect(() => {
		setRetentionPay(
			clone(
				content ?? {
					retention: {
						input: "Dollar",
						value: 0,
					},
					isGuaranteedDraw: false,
				}
			)
		);

		return () => {
			setRetentionPay({
				retention: {
					input: "Dollar",
					value: 0,
				},
				isGuaranteedDraw: false,
			});
			wasUpdated.current = false;
			clearSectionErrors?.(sectionId);
		};
	}, [sectionId, loading, content, setRetentionPay, clearSectionErrors]);

	const isPayRateData = payRateData?.length;
	const shouldDisablePayRatesButton = disabled || retentionPay?.["retention"]?.input !== "Dollar per Unit" || readOnly;

	return (
		<Stack sx={{ padding: "16px 32px 0px" }}>
			{loading ? (
				<>
					<Stack direction="row" spacing={2} mb={1} justifyContent="flex-end">
						<Skeleton variant="rounded" width={140} height={30} />
					</Stack>
					<Stack direction="row" spacing={2} mb={1}>
						<Skeleton variant="rounded" width={95} height={30} />
						<Skeleton variant="rounded" width={150} height={30} />
						<Skeleton variant="rounded" width={120} height={30} />
					</Stack>
				</>
			) : (
				<>
					<Stack direction="row" spacing={2}>
						<Stack>
							<InputFormExtension
								data={[
									{
										type: "Dollar",
										min: 0,
										max: 999999,
										decimalsEditable: true,
										decimals: 2,
									},
									{
										type: "Dollar per Unit",
										min: -99999,
										max: 99999,
										decimalsEditable: true,
										decimals: 2,
									},
									{
										type: "Percent",
										min: 0,
										max: 100,
										decimalsEditable: true,
										decimals: 2,
									},
								]}
								sx={{ width: "95px" }}
								defaultType={retentionPay?.["retention"]?.input || "Percent"}
								value={retentionPay?.["retention"]?.value || 0}
								error={localErrors?.some?.((error) => error?.retention)}
								disabled={disabled}
								readOnly={readOnly}
								onActionUpdate={(value) => handleUpdateInput("retention", "value", value)}
								onChangeType={(_, input) => {
									handleUpdateInput("retention", "input", input);
									setPayRateSelected(null);
								}}
							/>
						</Stack>
						<Stack width={150}>
							<SmartSelector
								selectBy={"value"}
								showBy={"label"}
								options={payRateData}
								inputProps={{
									placeholder: isPayRateData ? "Pay Rates" : "Pay Rates not Loaded",
								}}
								selected={String(payRateSelected)}
								disabled={shouldDisablePayRatesButton || !isPayRateData}
								loading={isLoadingPayRate}
								error={!!errorPayRate}
								onChangeAction={handleSelectChange}
							/>
						</Stack>
						<Stack>
							<Button
								variant="contained"
								onClick={handleButtonClick}
								className={clsx({
									"h-[30px] !rounded-[6px]": true,
									"!bg-primary hover:!shadow-primary-shadow-50": !shouldDisablePayRatesButton,
									"!bg-disabled": shouldDisablePayRatesButton,
								})}
								sx={{ width: "120px" }}
								disabled={shouldDisablePayRatesButton}
								readOnly={readOnly}
								disableElevation
							>
								<Typography
									className={clsx({
										"select-plan-dialog-assign-btn-text": true,
									})}
								>
									Load Pay Rates
								</Typography>
							</Button>
						</Stack>
					</Stack>

					{Boolean(errorsMessages?.length) && (
						<Stack mt={1}>
							{errorsMessages.map((message, key) => {
								const keyIndex = `retention-pay-error-${key}`;
								return <TierAlertError key={keyIndex} text={message} />;
							})}
						</Stack>
					)}
				</>
			)}
		</Stack>
	);
}

RetentionPay.propTypes = {
	sectionId: PropTypes.string,
	position: PropTypes.string,
	content: PropTypes.shape({
		retention: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		isGuaranteedDraw: PropTypes.bool,
	}),
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
